import React, { useState, useRef, useCallback } from "react";
import * as XLSX from "xlsx";
import { entities as entitiesApi, media as mediaApi } from "../services/api";
import {
  CloseIcon,
  UploadIcon,
  CheckIcon,
  AlertIcon,
  FileSpreadsheetIcon,
} from "./Icons";

/**
 * VineImportModal - Import products from Amazon Vine Itemized Report
 *
 * Expected Excel structure:
 * - Row 1: Title row (skip)
 * - Row 2: Headers
 * - Row 3+: Data
 *
 * Columns: A=Order Number, B=ASIN, C=Product Name, D=Order Type,
 *          E=Order Date, F=Shipped Date, G=Cancelled Date, H=Estimated Tax Value
 */
export default function VineImportModal({ isOpen, onClose, onComplete }) {
  const [step, setStep] = useState("upload"); // upload, preview, importing, done
  const [file, setFile] = useState(null);
  const [parsedItems, setParsedItems] = useState([]);
  const [skippedCount, setSkippedCount] = useState(0);
  const [, setImportProgress] = useState(0);
  const [imageProgress, setImageProgress] = useState(null); // { done, total, found }
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const resetState = useCallback(() => {
    setStep("upload");
    setFile(null);
    setParsedItems([]);
    setSkippedCount(0);
    setImportProgress(0);
    setImageProgress(null);
    setResults(null);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const parseExcelFile = useCallback(async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          // Convert to JSON, skipping row 1 (title) by starting from row 2
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: "",
          });

          // Skip title row (row 0) and header row (row 1), process data from row 2+
          const items = [];
          let skipped = 0;

          for (let i = 2; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (!row || row.length === 0) continue;

            const asin = row[1]?.toString().trim(); // Column B
            const productName = row[2]?.toString().trim(); // Column C
            const orderType = row[3]?.toString().trim(); // Column D
            const orderDate = row[4]; // Column E - Order Date
            const estimatedTaxValue = parseFloat(row[7]) || 0; // Column H

            // Skip if no ASIN
            if (!asin) {
              skipped++;
              continue;
            }

            // Skip cancellations
            if (orderType?.toUpperCase() === "CANCELLATION") {
              skipped++;
              continue;
            }

            // Calculate cost: Estimated Tax Value * 0.25
            const cost = estimatedTaxValue * 0.25;

            // Parse order date - Excel dates are numbers (days since 1900-01-01)
            let purchaseDate = null;
            if (orderDate) {
              if (typeof orderDate === "number") {
                // Excel serial date to JS date
                const excelEpoch = new Date(1899, 11, 30);
                const jsDate = new Date(
                  excelEpoch.getTime() + orderDate * 24 * 60 * 60 * 1000,
                );
                purchaseDate = jsDate.toISOString().split("T")[0];
              } else if (typeof orderDate === "string") {
                // Try to parse string date
                const parsed = new Date(orderDate);
                if (!isNaN(parsed.getTime())) {
                  purchaseDate = parsed.toISOString().split("T")[0];
                }
              }
            }

            items.push({
              sku: asin,
              name: productName || `Product ${asin}`,
              purchase_price: cost,
              purchase_date: purchaseDate,
              estimatedTaxValue,
              orderType,
            });
          }

          resolve({ items, skipped });
        } catch (err) {
          reject(new Error("Failed to parse Excel file: " + err.message));
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsArrayBuffer(file);
    });
  }, []);

  const handleFileSelect = useCallback(
    async (e) => {
      const selectedFile = e.target.files?.[0];
      if (!selectedFile) return;

      // Validate file type
      if (
        !selectedFile.name.endsWith(".xlsx") &&
        !selectedFile.name.endsWith(".xls")
      ) {
        setError("Please select an Excel file (.xlsx or .xls)");
        return;
      }

      setFile(selectedFile);
      setError(null);

      try {
        const { items, skipped } = await parseExcelFile(selectedFile);
        setParsedItems(items);
        setSkippedCount(skipped);
        setStep("preview");
      } catch (err) {
        setError(err.message);
        setFile(null);
      }
    },
    [parseExcelFile],
  );

  const handleImport = useCallback(async () => {
    if (parsedItems.length === 0) return;

    setStep("importing");
    setImportProgress(0);

    try {
      // Send items to backend for bulk import
      const { data } = await entitiesApi.bulkImport(parsedItems);
      console.log("Import results:", data);

      // Amazon's order emails no longer carry product images, so pull them
      // from the ASIN instead. Sequential on purpose: each call fetches from
      // Amazon's CDN and writes to R2, and Workers cap concurrent subrequests.
      const asins = parsedItems.map((item) => item.sku).filter(Boolean);
      let found = 0;
      setImageProgress({ done: 0, total: asins.length, found: 0 });

      for (let i = 0; i < asins.length; i += 1) {
        try {
          const res = await mediaApi.fromAsin(asins[i]);
          if (res?.data?.status === "created") found += 1;
        } catch (err) {
          // A missing image shouldn't fail the import.
          console.log("Image lookup failed for", asins[i], err.message);
        }
        setImageProgress({ done: i + 1, total: asins.length, found });
      }

      setResults({
        ...data,
        skippedCancellations: skippedCount,
        imagesFound: found,
        imagesAttempted: asins.length,
      });
      setStep("done");
      onComplete?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setStep("preview");
    }
  }, [parsedItems, skippedCount, onComplete]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const droppedFile = e.dataTransfer.files?.[0];
      if (droppedFile) {
        // Create a fake event to reuse handleFileSelect
        handleFileSelect({ target: { files: [droppedFile] } });
      }
    },
    [handleFileSelect],
  );

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={step === "importing" ? undefined : handleClose}
    >
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: "600px", width: "100%" }}
      >
        <div className="modal-header">
          <h2 style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FileSpreadsheetIcon size={20} />
            Import from Vine
          </h2>
          {step !== "importing" && (
            <button onClick={handleClose} className="btn btn-ghost btn-icon">
              <CloseIcon size={18} />
            </button>
          )}
        </div>

        <div className="modal-body">
          {error && (
            <div
              style={{
                padding: "var(--spacing-md)",
                background: "rgba(239, 68, 68, 0.1)",
                border: "1px solid var(--accent-danger)",
                borderRadius: "var(--radius-md)",
                marginBottom: "var(--spacing-md)",
                display: "flex",
                alignItems: "center",
                gap: "var(--spacing-sm)",
                color: "var(--accent-danger)",
              }}
            >
              <AlertIcon size={18} />
              {error}
            </div>
          )}

          {/* Upload Step */}
          {step === "upload" && (
            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: "2px dashed var(--border-color)",
                borderRadius: "var(--radius-lg)",
                padding: "var(--spacing-2xl)",
                textAlign: "center",
                cursor: "pointer",
                transition: "border-color 0.2s, background 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--accent-primary)";
                e.currentTarget.style.background = "var(--bg-hover)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--border-color)";
                e.currentTarget.style.background = "transparent";
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />
              <UploadIcon size={48} style={{ opacity: 0.5 }} />
              <p style={{ marginTop: "var(--spacing-md)", fontWeight: 500 }}>
                Drop your Vine report here or click to browse
              </p>
              <p
                style={{
                  marginTop: "var(--spacing-sm)",
                  color: "var(--text-muted)",
                  fontSize: "0.875rem",
                }}
              >
                Accepts .xlsx files from Amazon Vine Itemized Report
              </p>
            </div>
          )}

          {/* Preview Step */}
          {step === "preview" && (
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "var(--spacing-md)",
                }}
              >
                <div>
                  <p style={{ fontWeight: 500 }}>{file?.name}</p>
                  <p
                    style={{
                      color: "var(--text-muted)",
                      fontSize: "0.875rem",
                    }}
                  >
                    {parsedItems.length} items to import
                    {skippedCount > 0 && ` (${skippedCount} skipped)`}
                  </p>
                </div>
                <button onClick={resetState} className="btn btn-ghost btn-sm">
                  Change file
                </button>
              </div>

              {/* Preview table */}
              <div
                style={{
                  maxHeight: "300px",
                  overflow: "auto",
                  border: "1px solid var(--border-color)",
                  borderRadius: "var(--radius-md)",
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr
                      style={{
                        background: "var(--bg-secondary)",
                        position: "sticky",
                        top: 0,
                      }}
                    >
                      <th
                        style={{
                          padding: "var(--spacing-sm)",
                          textAlign: "left",
                          borderBottom: "1px solid var(--border-color)",
                        }}
                      >
                        ASIN
                      </th>
                      <th
                        style={{
                          padding: "var(--spacing-sm)",
                          textAlign: "left",
                          borderBottom: "1px solid var(--border-color)",
                        }}
                      >
                        Product Name
                      </th>
                      <th
                        style={{
                          padding: "var(--spacing-sm)",
                          textAlign: "right",
                          borderBottom: "1px solid var(--border-color)",
                        }}
                      >
                        Cost
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedItems.slice(0, 50).map((item, idx) => (
                      <tr
                        key={idx}
                        style={{
                          borderBottom: "1px solid var(--border-color)",
                        }}
                      >
                        <td
                          style={{
                            padding: "var(--spacing-sm)",
                            fontFamily: "var(--font-mono)",
                            fontSize: "0.8rem",
                          }}
                        >
                          {item.sku}
                        </td>
                        <td
                          style={{
                            padding: "var(--spacing-sm)",
                            maxWidth: "250px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.name}
                        </td>
                        <td
                          style={{
                            padding: "var(--spacing-sm)",
                            textAlign: "right",
                          }}
                        >
                          ${item.purchase_price.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsedItems.length > 50 && (
                  <p
                    style={{
                      padding: "var(--spacing-sm)",
                      textAlign: "center",
                      color: "var(--text-muted)",
                      fontSize: "0.875rem",
                    }}
                  >
                    ... and {parsedItems.length - 50} more items
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Importing Step */}
          {step === "importing" && (
            <div style={{ textAlign: "center", padding: "var(--spacing-xl)" }}>
              <div className="spinner" style={{ margin: "0 auto" }}></div>
              <p style={{ marginTop: "var(--spacing-md)" }}>
                {imageProgress
                  ? `Fetching product images… ${imageProgress.done}/${imageProgress.total}`
                  : `Importing ${parsedItems.length} items...`}
              </p>
              {imageProgress && (
                <>
                  <div
                    style={{
                      height: "6px",
                      background: "var(--bg-secondary)",
                      borderRadius: "3px",
                      overflow: "hidden",
                      margin: "var(--spacing-md) auto 0",
                      maxWidth: "320px",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${imageProgress.total ? (imageProgress.done / imageProgress.total) * 100 : 0}%`,
                        background: "var(--primary, #3b82f6)",
                        transition: "width 0.2s",
                      }}
                    />
                  </div>
                  <p
                    style={{
                      marginTop: "var(--spacing-sm)",
                      fontSize: "0.85rem",
                      color: "var(--text-muted)",
                    }}
                  >
                    {imageProgress.found} image
                    {imageProgress.found === 1 ? "" : "s"} found so far
                  </p>
                </>
              )}
            </div>
          )}

          {/* Done Step */}
          {step === "done" && results && (
            <div style={{ textAlign: "center", padding: "var(--spacing-xl)" }}>
              <div
                style={{
                  width: "64px",
                  height: "64px",
                  borderRadius: "50%",
                  background: "rgba(34, 197, 94, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                  color: "var(--accent-success)",
                }}
              >
                <CheckIcon size={32} />
              </div>
              <h3 style={{ marginTop: "var(--spacing-md)" }}>
                Import Complete
              </h3>
              <div
                style={{
                  marginTop: "var(--spacing-md)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--spacing-xs)",
                  color: "var(--text-secondary)",
                }}
              >
                <p>
                  <strong>{results.created}</strong> items created
                </p>
                <p>
                  <strong>{results.updated}</strong> items updated
                </p>
                {results.skippedCancellations > 0 && (
                  <p>
                    <strong>{results.skippedCancellations}</strong>{" "}
                    cancellations skipped
                  </p>
                )}
                {results.imagesAttempted > 0 && (
                  <div style={{ marginBottom: "var(--spacing-xs)" }}>
                    <strong>{results.imagesFound}</strong> of{" "}
                    <strong>{results.imagesAttempted}</strong> product images
                    fetched from Amazon
                  </div>
                )}

                {results.skipped > 0 && (
                  <p>
                    <strong>{results.skipped}</strong> items skipped (no ASIN)
                  </p>
                )}
                {results.errors?.length > 0 && (
                  <>
                    <p style={{ color: "var(--accent-danger)" }}>
                      <strong>{results.errors.length}</strong> errors
                    </p>
                    <div
                      style={{
                        marginTop: "var(--spacing-sm)",
                        maxHeight: "150px",
                        overflow: "auto",
                        background: "var(--bg-secondary)",
                        borderRadius: "var(--radius-sm)",
                        padding: "var(--spacing-sm)",
                        fontSize: "0.75rem",
                        textAlign: "left",
                      }}
                    >
                      {results.errors.slice(0, 20).map((err, idx) => (
                        <div key={idx} style={{ marginBottom: "4px" }}>
                          <strong>{err.sku}:</strong> {err.error}
                        </div>
                      ))}
                      {results.errors.length > 20 && (
                        <div style={{ color: "var(--text-muted)" }}>
                          ... and {results.errors.length - 20} more
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {step === "upload" && (
            <button onClick={handleClose} className="btn btn-secondary">
              Cancel
            </button>
          )}
          {step === "preview" && (
            <>
              <button onClick={handleClose} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleImport}
                className="btn btn-primary"
                disabled={parsedItems.length === 0}
              >
                Import {parsedItems.length} Items
              </button>
            </>
          )}
          {step === "done" && (
            <button onClick={handleClose} className="btn btn-primary">
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
